import React from "react";
import { Box, Container, Heading, Text, VStack, SimpleGrid, Progress, HStack, Icon, Badge, List, ListItem, ListIcon, Divider } from "@chakra-ui/react";
import { FaBolt, FaHome, FaIndustry, FaCheckCircle, FaBatteryFull, FaTools, FaRecycle } from "react-icons/fa";
import { brandConfig } from "../../../brandConfig";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { SolarFooter } from "../../../components/chakra/SolarFooter/SolarFooter";
import { SEOHead } from "../../../components/SEOHead";

const SOLAR_GOLD = "#C69214";
const NAVY = "#141C49";
const SOLAR_GOLD_HOVER = "#a07e10";

const SolarResourcesBatteryGuide: React.FC = () => {
  const batteryScenarios = [
    {
      title: "Essential Backup",
      capacity: "5kWh",
      description: "Keep the lights on during outages",
      coverage: 40,
      devices: ["LED Lighting", "WiFi Router", "Phone Charging", "Small Fridge"],
      runtime: "4-6 hours",
      bestFor: "Basic emergency backup",
      icon: FaBolt
    },
    {
      title: "Home Comfort",
      capacity: "10kWh",
      description: "Power essential appliances overnight",
      coverage: 65,
      devices: ["All Lights", "Fridge & Freezer", "TV & WiFi", "Coffee Machine", "Laptop Charging"],
      runtime: "8-12 hours",
      bestFor: "Evening and overnight usage",
      icon: FaHome
    },
    {
      title: "Full Independence",
      capacity: "15kWh",
      description: "Run your entire home off-grid",
      coverage: 85,
      devices: ["All Appliances", "Air Conditioning", "Pool Pump", "Electric Oven", "Washing Machine"],
      runtime: "12-18 hours",
      bestFor: "Complete energy independence",
      icon: FaBatteryFull
    },
    {
      title: "Commercial Scale",
      capacity: "30kWh+",
      description: "Business continuity and load shifting",
      coverage: 100,
      devices: ["Office Equipment", "HVAC Systems", "Industrial Equipment", "Server Rooms", "Workshop Tools"],
      runtime: "24+ hours",
      bestFor: "Businesses and large properties",
      icon: FaIndustry
    }
  ];

  const lifespanData = [
    {
      brand: "Tesla Powerwall 2",
      cycles: "7,000",
      warranty: "10 years",
      retention: "70%",
      efficiency: "90%"
    },
    {
      brand: "LG Chem RESU",
      cycles: "6,000",
      warranty: "10 years",
      retention: "60%",
      efficiency: "95%"
    },
    {
      brand: "Sonnen Eco",
      cycles: "10,000",
      warranty: "10 years",
      retention: "70%",
      efficiency: "92%"
    },
    {
      brand: "BYD Battery-Box",
      cycles: "6,000",
      warranty: "10 years",
      retention: "80%",
      efficiency: "96%"
    }
  ];

  return (
    <Box minH="100vh" bg="white">
      <SEOHead
        pagePath="/solar/resources/battery-guide"
        fallbackTitle="Battery Storage Guide"
        fallbackDescription="Comprehensive guide to solar battery sizing, capacity, and maintenance"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={20}>
        <Container maxW="container.xl">
          <VStack spacing={6} textAlign="center">
            <Text fontSize="sm" color="rgba(255, 255, 255, 0.7)" textTransform="uppercase" letterSpacing="wider">
              Resources
            </Text>
            <Heading size="2xl" fontWeight="bold">
              Battery Storage Guide
            </Heading>
            <Text fontSize="xl" color="rgba(255, 255, 255, 0.9)" maxW="2xl">
              Everything you need to know about solar battery capacity, sizing, and maintenance
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Battery Sizing Guide */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Choose Your Battery Capacity</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
            Select the right battery size based on your energy needs and backup requirements
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
          {batteryScenarios.map((scenario) => (
            <Box
              key={scenario.title}
              p={8}
              bg="white"
              borderRadius="2xl"
              border="2px solid"
              borderColor={SOLAR_GOLD}
              shadow="md"
              _hover={{ shadow: "xl", transform: "translateY(-4px)" }}
              transition="all 0.3s"
            >
              <HStack justify="space-between" mb={4}>
                <HStack>
                  <Icon as={scenario.icon} boxSize={8} color={SOLAR_GOLD} />
                  <Heading size="md">{scenario.title}</Heading>
                </HStack>
                <Badge bg={NAVY} color="white" fontSize="lg" px={4} py={2}>
                  {scenario.capacity}
                </Badge>
              </HStack>

              <Text color="gray.600" mb={6} fontSize="lg">
                {scenario.description}
              </Text>

              <VStack align="stretch" spacing={4}>
                <Box>
                  <HStack justify="space-between" mb={2}>
                    <Text fontWeight="semibold" fontSize="sm">Coverage</Text>
                    <Text fontWeight="bold" color={NAVY}>{scenario.coverage}%</Text>
                  </HStack>
                  <Progress value={scenario.coverage} colorScheme="yellow" size="lg" borderRadius="full" />
                </Box>

                <Divider />

                <Box>
                  <Text fontWeight="semibold" mb={3}>Powers These Devices:</Text>
                  <List spacing={2}>
                    {scenario.devices.map((device) => (
                      <ListItem key={device} fontSize="sm">
                        <HStack>
                          <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                          <Text>{device}</Text>
                        </HStack>
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Divider />

                <HStack justify="space-between">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500">Typical Runtime</Text>
                    <Text fontWeight="bold">{scenario.runtime}</Text>
                  </VStack>
                  <VStack align="end" spacing={0}>
                    <Text fontSize="xs" color="gray.500">Best For</Text>
                    <Text fontWeight="bold" fontSize="sm" textAlign="right">{scenario.bestFor}</Text>
                  </VStack>
                </HStack>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      </Container>

      {/* Battery Lifespan Comparison */}
      <Box bg="gray.50" py={16}>
        <Container maxW="container.xl">
          <VStack spacing={8} mb={12}>
            <Heading size="xl" textAlign="center">Battery Lifespan & Cycle Life</Heading>
            <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
              Compare warranty periods, cycle life, and performance retention across leading battery brands
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
            {lifespanData.map((battery) => (
              <Box
                key={battery.brand}
                p={6}
                bg="white"
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                shadow="sm"
              >
                <VStack align="stretch" spacing={4}>
                  <Heading size="sm" color={brandConfig.colors.primary} minH="40px">
                    {battery.brand}
                  </Heading>

                  <Divider />

                  <VStack align="stretch" spacing={3}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Cycle Life</Text>
                      <Text fontWeight="bold">{battery.cycles}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Warranty</Text>
                      <Text fontWeight="bold">{battery.warranty}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Retention</Text>
                      <Text fontWeight="bold" color={SOLAR_GOLD}>{battery.retention}</Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Efficiency</Text>
                      <Text fontWeight="bold" color={NAVY}>{battery.efficiency}</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>

          <Box mt={8} p={6} bg="gray.50" borderRadius="xl" border="1px solid" borderColor={SOLAR_GOLD}>
            <HStack spacing={4} align="start">
              <Icon as={FaRecycle} boxSize={6} color={SOLAR_GOLD} mt={1} />
              <VStack align="start" spacing={2}>
                <Text fontWeight="bold" color={NAVY}>Understanding Cycle Life</Text>
                <Text color="gray.700" fontSize="sm">
                  One cycle = fully charging and discharging the battery. Most lithium batteries are rated for 6,000-10,000 cycles,
                  which typically means 10-15 years of daily use before capacity drops to warranty minimum (usually 60-80% of original capacity).
                </Text>
              </VStack>
            </HStack>
          </Box>
        </Container>
      </Box>

      {/* Maintenance Tips */}
      <Container maxW="container.xl" py={16}>
        <VStack spacing={8} mb={12}>
          <Heading size="xl" textAlign="center">Battery Maintenance Tips</Heading>
          <Text fontSize="lg" color="gray.600" textAlign="center" maxW="3xl">
            Maximize your battery's lifespan and performance with proper care
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
          <Box
            p={8}
            bg="gray.50"
            borderRadius="2xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
          >
            <Icon as={FaTools} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={4}>Regular Monitoring</Heading>
            <List spacing={3}>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Check battery app weekly for performance data
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Monitor charge/discharge cycles
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Watch for unusual temperature readings
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Review energy consumption patterns
              </ListItem>
            </List>
          </Box>

          <Box
            p={8}
            bg="gray.50"
            borderRadius="2xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
          >
            <Icon as={FaBatteryFull} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={4}>Optimal Usage</Heading>
            <List spacing={3}>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Keep charge between 20-80% for longevity
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Avoid extreme temperatures when possible
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Use battery management system features
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Optimize charge/discharge timing
              </ListItem>
            </List>
          </Box>

          <Box
            p={8}
            bg="gray.50"
            borderRadius="2xl"
            border="2px solid"
            borderColor={SOLAR_GOLD}
          >
            <Icon as={FaCheckCircle} boxSize={12} color={SOLAR_GOLD} mb={4} />
            <Heading size="md" mb={4}>Professional Service</Heading>
            <List spacing={3}>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Annual inspection by certified technician
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Firmware updates when available
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Connection and ventilation checks
              </ListItem>
              <ListItem fontSize="sm" color="gray.700">
                <ListIcon as={FaCheckCircle} color={SOLAR_GOLD} />
                Warranty compliance documentation
              </ListItem>
            </List>
          </Box>
        </SimpleGrid>
      </Container>

      {/* CTA Section */}
      <Box bg={brandConfig.colors.primary} color="white" py={16}>
        <Container maxW="container.lg">
          <VStack spacing={6} textAlign="center">
            <Heading size="xl">Ready to Add Battery Storage?</Heading>
            <Text fontSize="lg" maxW="2xl" color="rgba(255, 255, 255, 0.9)">
              Our energy experts will help you choose the perfect battery size for your home or business
            </Text>
            <HStack spacing={4}>
              <Box
                as="button"
                px={8}
                py={4}
                bg={SOLAR_GOLD}
                color="white"
                fontWeight="bold"
                borderRadius="lg"
                _hover={{ bg: SOLAR_GOLD_HOVER }}
              >
                Get Battery Quote
              </Box>
              <Box
                as="button"
                px={8}
                py={4}
                bg="transparent"
                color="white"
                fontWeight="bold"
                borderRadius="lg"
                border="2px solid white"
                _hover={{ bg: "whiteAlpha.200" }}
              >
                Calculate Savings
              </Box>
            </HStack>
          </VStack>
        </Container>
      </Box>

      <SolarFooter />
    </Box>
  );
};

export default SolarResourcesBatteryGuide;
